import { ARCHETYPE_BY_ID } from "@nano/content";
import type { AvatarId, PlayerSnapshot } from "@nano/contracts";
import { defaultGameConfig, firstClearGold, type HouseId } from "@nano/game-config";
import { SAVE_VERSION, type OutboxEntry, type SaveData, type Stars } from "./types.ts";

export const levelKey = (houseId: HouseId, levelId: number) => `${houseId}:${levelId}`;

export function emptySave(guestId: string, now: number): SaveData {
  return {
    version: SAVE_VERSION,
    profile: {
      guestId,
      avatarId: null,
      displayName: null,
      createdAt: now,
      seenTutorials: [],
      cityHintDone: false,
      profileDirty: false,
    },
    session: null,
    progress: {},
    server: null,
    equipped: {},
    equipmentDirty: false,
    settings: { sound: true, music: true, vibration: true, maxDpr: 2 },
    leaderboard: null,
    syncMeta: { lastSyncedAt: null, sessionLost: false },
    stats: { playDayCount: 0, lastPlayDay: null, bestCombo: 0 },
    announcedAchievements: [],
    wishlist: null,
    prefs: { lastRank: null, installHintDismissedAt: null },
  };
}

/** Local calendar day, e.g. "2026-10-24" (device clock; badges only). */
export function dayKey(now: number): string {
  const date = new Date(now);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Counts the day once and keeps the best combo; called when a level is won. */
export function recordPlayStats(save: SaveData, now: number, combo: number): SaveData {
  const day = dayKey(now);
  const newDay = save.stats.lastPlayDay !== day;
  const bestCombo = Math.max(save.stats.bestCombo, combo);
  if (!newDay && bestCombo === save.stats.bestCombo) return save;
  return {
    ...save,
    stats: {
      playDayCount: save.stats.playDayCount + (newDay ? 1 : 0),
      lastPlayDay: day,
      bestCombo,
    },
  };
}

export function withAvatar(save: SaveData, avatarId: AvatarId): SaveData {
  const archetype = ARCHETYPE_BY_ID.get(avatarId);
  const previous = save.profile.avatarId ? ARCHETYPE_BY_ID.get(save.profile.avatarId) : undefined;
  const head =
    !save.equipped.head || save.equipped.head === previous?.starterItemId ? archetype?.starterItemId : save.equipped.head;
  return {
    ...save,
    profile: { ...save.profile, avatarId, profileDirty: save.session !== null || save.profile.profileDirty },
    equipped: { ...save.equipped, ...(head ? { head } : {}) },
  };
}

export function bestStars(save: SaveData, houseId: HouseId, levelId: number): Stars | null {
  return save.progress[houseId]?.[levelId]?.bestStars ?? null;
}

export function highestCompleted(save: SaveData, houseId: HouseId): number {
  const levels = save.progress[houseId];
  if (!levels) return 0;
  // Levels unlock in order, so the highest contiguous clear is the checkpoint.
  let n = 0;
  while (levels[n + 1]) n += 1;
  return n;
}

export function nextLevel(save: SaveData, houseId: HouseId): number {
  return Math.min(100, highestCompleted(save, houseId) + 1);
}

export function isUnlocked(save: SaveData, houseId: HouseId, levelId: number): boolean {
  return levelId === 1 || bestStars(save, houseId, levelId - 1) !== null;
}

export function totalStars(save: SaveData): number {
  return Object.values(save.progress).reduce(
    (sum, levels) => sum + Object.values(levels ?? {}).reduce((s, level) => s + level.bestStars, 0),
    0,
  );
}

const fasterOf = (a: number | null | undefined, b: number | null | undefined): number | null =>
  a == null ? (b ?? null) : b == null ? a : Math.min(a, b);

export function bestTime(save: SaveData, houseId: HouseId, levelId: number): number | null {
  return save.progress[houseId]?.[levelId]?.bestTimeMs ?? null;
}

/** Stars only go up; the time record only goes down. */
export function recordLocalResult(
  save: SaveData,
  houseId: HouseId,
  levelId: number,
  stars: Stars,
  timeMs: number | null = null,
): SaveData {
  const house = save.progress[houseId] ?? {};
  const previous = house[levelId];
  const bestStars = Math.max(previous?.bestStars ?? 0, stars) as Stars;
  const bestTimeMs = fasterOf(previous?.bestTimeMs, timeMs);
  if (previous && previous.bestStars === bestStars && (previous.bestTimeMs ?? null) === bestTimeMs) return save;
  return { ...save, progress: { ...save.progress, [houseId]: { ...house, [levelId]: { bestStars, bestTimeMs } } } };
}

/**
 * Gold we expect the server to grant for a new result, shown as
 * "waiting for sync" until the Worker confirms it. Mirrors the server rules
 * but is never treated as real gold.
 */
export function predictGold(
  save: SaveData,
  outbox: readonly OutboxEntry[],
  houseId: HouseId,
  levelId: number,
  stars: Stars,
): number {
  const economy = defaultGameConfig.economy;
  const key = levelKey(houseId, levelId);
  const pendingStars = outbox
    .filter((entry) => entry.status === "pending" && entry.houseId === houseId && entry.levelId === levelId)
    .reduce((max, entry) => Math.max(max, entry.stars), 0);
  const known = Math.max(save.server?.verified[key] ?? 0, pendingStars);
  if (known === 0) return firstClearGold(economy, levelId) + (stars === 3 ? economy.firstThreeStarBonusGold : 0);
  if (stars === 3 && known < 3) return economy.firstThreeStarBonusGold;
  return 0;
}

export function pendingGold(outbox: readonly OutboxEntry[]): number {
  return outbox.filter((entry) => entry.status === "pending").reduce((sum, entry) => sum + entry.predictedGold, 0);
}

/** Server snapshot wins for gold and ownership; local progress only grows. */
export function applySnapshot(save: SaveData, snapshot: PlayerSnapshot): SaveData {
  let progress = save.progress;
  const verified: Record<string, Stars> = {};
  for (const level of snapshot.levels) {
    verified[levelKey(level.houseId, level.levelId)] = level.bestStars;
    const house = progress[level.houseId] ?? {};
    const local = house[level.levelId];
    const stars = Math.max(local?.bestStars ?? 0, level.bestStars) as Stars;
    const time = fasterOf(local?.bestTimeMs, level.bestTimeMs);
    if (!local || local.bestStars !== stars || (local.bestTimeMs ?? null) !== time) {
      progress = { ...progress, [level.houseId]: { ...house, [level.levelId]: { bestStars: stars, bestTimeMs: time } } };
    }
  }
  return {
    ...save,
    progress,
    profile: {
      ...save.profile,
      avatarId: save.profile.profileDirty ? save.profile.avatarId : snapshot.avatarId,
      displayName: save.profile.profileDirty ? save.profile.displayName : snapshot.displayName,
    },
    server: {
      playerId: snapshot.playerId,
      publicTag: snapshot.publicTag,
      wallet: snapshot.wallet,
      owned: snapshot.owned,
      hasRecoveryCode: snapshot.hasRecoveryCode,
      verified,
      claimedAchievements: snapshot.claimedAchievements,
    },
    equipped: save.equipmentDirty ? save.equipped : snapshot.equipped,
  };
}

/** A restored journey replaces the local one entirely (no merging of two guests). */
export function saveFromSnapshot(base: SaveData, snapshot: PlayerSnapshot, session: SaveData["session"]): SaveData {
  const fresh: SaveData = {
    ...base,
    profile: { ...base.profile, profileDirty: false, avatarId: snapshot.avatarId, displayName: snapshot.displayName },
    session,
    progress: {},
    server: null,
    equipped: {},
    equipmentDirty: false,
    leaderboard: null,
    syncMeta: { lastSyncedAt: Date.now(), sessionLost: false },
  };
  return applySnapshot(fresh, snapshot);
}

export function ownedItems(save: SaveData): string[] {
  const starters = [...ARCHETYPE_BY_ID.values()].map((a) => a.starterItemId);
  return [...new Set([...starters, ...(save.server?.owned ?? [])])];
}
